/**
 * The single source of chart colour for the partners portal. Pages must never
 * pass hex values to a chart component.
 *
 * Values were produced with the dataviz skill's validator against the light
 * chart surface and pass all-pairs CVD separation (worst 9.7 dE), the
 * normal-vision floor (worst 20.3 dE) and 3:1 contrast. Do not substitute
 * colours without re-running:
 *   node scripts/validate_palette.js "<hex,...>" --mode light --pairs all
 */

/** Identity series. Assigned in fixed order, never cycled. */
export const CATEGORICAL = ['#6D5AE6', '#0E9BB5', '#D97706', '#DB2777'] as const;

/** Slot 5 and beyond. A fifth series folds into "Other", never a new hue. */
export const OTHER = '#64748B';

/** Reserved. Always rendered alongside a text label, never colour alone. */
export const STATUS = {
  good: '#059669',
  warning: '#B45309',
  critical: '#DC2626',
  neutral: '#64748B',
} as const;

export function categoricalColor(index: number): string {
  return index < CATEGORICAL.length ? CATEGORICAL[index] : OTHER;
}

/**
 * Every status the back-office status pies can receive, transcribed from
 * kashround-api/app/constants.php. Keep this map in step with those constant
 * lists: SUBSCRIPTION_STATUSES, PAYMENT_STATUSES and INVOICE_STATUSES.
 * e2e/charts/status-colors.spec.ts reads that file and fails if a status is
 * added there without a colour here.
 *
 * A status that is genuinely informational rather than good or bad (an
 * unissued draft, a voided invoice) maps to neutral deliberately.
 */
export const STATUS_BY_LABEL: Record<string, keyof typeof STATUS> = {
  // SUBSCRIPTION_STATUSES
  active: 'good',
  pending_payment: 'warning',
  expired: 'critical',
  canceled: 'critical',
  // Critical rather than warning: a suspended group is blocked from every
  // money-in and growth action, which is an outage for them, not a nudge.
  suspended: 'critical',

  // PAYMENT_STATUSES
  pending: 'warning',
  successful: 'good',
  failed: 'critical',
  timed_out: 'critical',

  // INVOICE_STATUSES
  draft: 'neutral',
  sent: 'warning',
  paid: 'good',
  void: 'neutral',
};

/**
 * Maps a status string from the API to a reserved status colour. Unknown
 * labels fall back to neutral rather than borrowing a categorical hue, so a
 * new backend status can never silently read as an identity series.
 */
export function statusColorKey(label: string): keyof typeof STATUS {
  return STATUS_BY_LABEL[label?.toLowerCase()] ?? 'neutral';
}

export function statusColor(label: string): string {
  return STATUS[statusColorKey(label)];
}

/** Severity order for status series. Alphabetical within a severity. */
const STATUS_RANK: Record<keyof typeof STATUS, number> = {
  good: 0,
  warning: 1,
  critical: 2,
  neutral: 3,
};

/**
 * Deterministic order for a status series. The API groups by status with no
 * ORDER BY, so row order is whatever the database returns and can differ
 * between loads. Sorted by severity rather than by count, so the sequence
 * stays semantic (healthy first, problems next) instead of reshuffling as
 * the underlying counts move.
 */
export function sortStatusRows<T extends Record<string, unknown>>(rows: T[], nameKey: string): T[] {
  return [...rows].sort((a, b) => {
    const aLabel = String(a[nameKey] ?? '');
    const bLabel = String(b[nameKey] ?? '');
    const rank = STATUS_RANK[statusColorKey(aLabel)] - STATUS_RANK[statusColorKey(bLabel)];
    return rank !== 0 ? rank : aLabel.localeCompare(bLabel);
  });
}

/**
 * Deterministic order for a categorical series. Identity hues are assigned by
 * index, so an unstable row order repaints the same category a different
 * colour on the next load. Alphabetical by label is arbitrary but stable.
 */
export function sortCategoricalRows<T extends Record<string, unknown>>(rows: T[], nameKey: string): T[] {
  return [...rows].sort((a, b) => String(a[nameKey] ?? '').localeCompare(String(b[nameKey] ?? '')));
}

/** The label used for the folded overflow bucket. */
export const OTHER_LABEL = 'Other';

/**
 * Keeps the top {limit} rows by value and sums the rest into a single "Other"
 * row. categoricalColor() only has four identity hues and returns the same
 * grey for every index beyond them, so without this a six-tier pie renders
 * three separate slices in one indistinguishable colour. Overflow collapses
 * into ONE bucket, matching the palette contract.
 *
 * This sums counts inside one category, never money across currencies.
 *
 * The result is itself deterministically ordered (value descending, label
 * ascending on ties, "Other" last), so callers do not need to sort again.
 */
export function foldToOther<T extends Record<string, unknown>>(
  rows: T[],
  nameKey: string,
  valueKey: string,
  limit: number = CATEGORICAL.length,
): Array<Record<string, unknown>> {
  const ranked = [...rows].sort((a, b) => {
    const byValue = Number(b[valueKey] ?? 0) - Number(a[valueKey] ?? 0);
    return byValue !== 0 ? byValue : String(a[nameKey] ?? '').localeCompare(String(b[nameKey] ?? ''));
  });

  if (ranked.length <= limit) return ranked;

  const kept = ranked.slice(0, limit);
  const folded = ranked.slice(limit).reduce((sum, row) => sum + Number(row[valueKey] ?? 0), 0);

  return [...kept, { [nameKey]: OTHER_LABEL, [valueKey]: folded }];
}

/** Shared recharts <Tooltip contentStyle> so tooltips match the card surface. */
export const CHART_TOOLTIP_STYLE = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  color: 'hsl(var(--card-foreground))',
} as const;
